/**
 * DMS Carousel Templates — Phase 1 + Phase 3.
 *
 * Phase 1: pure design-token templates (no user imagery).
 * Phase 3: photo templates that consume a user-uploaded image plus its
 *          focal_zone (detected by Claude Vision) to place text in the
 *          empty half of the frame.
 *
 * Contract for each template:
 *   id, name, thumbnail, needsPhoto:
 *     Meta shown in the picker. `needsPhoto: true` triggers the "Add photos"
 *     panel on the slide builder.
 *   layout(slide, brand, slideKind, meta):
 *     Returns a React node representing the slide's inner content. `slideKind`
 *     is "cover" | "body" | "final". `meta` may include { index, total, photo }
 *     where `photo` = { url, focal: { subject_zone, safe_zone } }.
 *   background(brand, slideKind):
 *     Returns the CSS `background` value for the slide (photo templates
 *     produce a solid colour here — the image is layered on top by the
 *     renderer so we can control positioning + scrim).
 */

const SLIDE_W = 1080;
const SLIDE_H = 1350;

// Preview dimensions (2:2.5 ratio preserved). We render at 100% then scale down
// via CSS transform so html-to-image captures the full 1080×1350 output.
export const PREVIEW_SCALE = 0.5;

// Default slide dimensions (Instagram carousel portrait). Individual templates
// may override via a top-level `dimensions` field — e.g. reels are 1080×1920.
export const SLIDE_DIMENSIONS = { width: SLIDE_W, height: SLIDE_H };
export const REEL_DIMENSIONS = { width: 1080, height: 1920 };

// Instagram Reels safe zone — content outside these bounds gets covered by
// the IG UI (username/menu on top, caption/music at bottom, action buttons on
// the right). Values are Meta's official 2024 guidance for 1080×1920 frames.
export const REEL_SAFE_ZONE = {
  top: 220,
  bottom: 380,
  right: 220,
  left: 60,
};

// Convenience for templates + renderers that need a template's dimensions.
export function getTemplateDimensions(templateId) {
  const t = TEMPLATES.find(x => x.id === templateId);
  return (t && t.dimensions) || SLIDE_DIMENSIONS;
}


/* ------------------------------------------------------------------- Helpers */

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const int = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Common style scaffolding all templates reuse.
const baseSlideStyle = (brand) => ({
  width: `${SLIDE_W}px`,
  height: `${SLIDE_H}px`,
  fontFamily: brand.font_body || "Inter, system-ui, sans-serif",
  position: "relative",
  overflow: "hidden",
  boxSizing: "border-box",
});

function LogoBadge({ brand, tone = "light" }) {
  if (!brand.logo_url || brand.logo_position === "none") return null;
  const positions = {
    "top-left":     { top: 60, left: 60 },
    "top-right":    { top: 60, right: 60 },
    "bottom-left":  { bottom: 60, left: 60 },
    "bottom-right": { bottom: 60, right: 60 },
  };
  const pos = positions[brand.logo_position] || positions["bottom-left"];
  return (
    <img
      src={brand.logo_url}
      alt=""
      crossOrigin="anonymous"
      style={{
        position: "absolute", ...pos, height: 80, width: "auto", maxWidth: 240,
        opacity: tone === "dark" ? 0.92 : 0.85, objectFit: "contain",
      }}
    />
  );
}

function SlideNumber({ index, total, brand, tone = "light" }) {
  return (
    <div
      style={{
        position: "absolute", bottom: 60, right: 60,
        fontSize: 28, fontWeight: 600, letterSpacing: "0.14em",
        color: tone === "dark" ? hexToRgba(brand.text_on_primary || "#FFFFFF", 0.65)
                               : hexToRgba(brand.text_on_light || "#0A2342", 0.55),
        fontFamily: brand.font_body,
      }}
    >
      {String(index).padStart(2, "0")}/{String(total).padStart(2, "0")}
    </div>
  );
}


/* --------------------------------------------------------------- Templates */

const TEMPLATES = [
  /* 1 — Minimal White */
  {
    id: "minimal-white",
    name: "Minimal White",
    thumbnail: {
      bg: "#FFFFFF", accent: "#0A2342",
      preview: "Clean, editorial, brand-accent bar. Best for education carousels.",
    },
    background: (brand) => `#FFFFFF`,
    layout: (slide, brand, kind, meta) => {
      const accent = brand.accent_color || "#F59E0B";
      const heading = brand.font_heading || "Playfair Display, serif";
      const text = brand.text_on_light || "#0A2342";
      const soft = hexToRgba(text, 0.65);

      if (kind === "cover") {
        return (
          <div style={{ padding: "120px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
            <div style={{ width: 120, height: 8, background: accent, borderRadius: 4 }} />
            <div>
              <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1.02, color: text, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
                {slide.headline}
              </h1>
              {slide.subhead && (
                <p style={{ fontSize: 40, lineHeight: 1.35, color: soft, marginTop: 40, fontWeight: 400 }}>
                  {slide.subhead}
                </p>
              )}
            </div>
            <div style={{ fontSize: 24, letterSpacing: "0.28em", textTransform: "uppercase", color: soft, fontWeight: 600 }}>
              Swipe →
            </div>
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ padding: "140px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "center", textAlign: "center" }}>
            <h1 style={{ fontFamily: heading, fontSize: 96, lineHeight: 1.08, color: text, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
              {slide.headline}
            </h1>
            <div style={{ width: 100, height: 6, background: accent, margin: "56px auto", borderRadius: 3 }} />
            <p style={{ fontSize: 44, lineHeight: 1.3, color: text, fontWeight: 500 }}>{slide.cta}</p>
          </div>
        );
      }
      // body slide
      return (
        <div style={{ padding: "120px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "flex-start" }}>
          <div style={{ fontSize: 24, letterSpacing: "0.28em", textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 32 }}>
            {String(meta.index).padStart(2, "0")}
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 84, lineHeight: 1.06, color: text, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: 40, lineHeight: 1.4, color: soft, marginTop: 48, fontWeight: 400 }}>
            {slide.body}
          </p>
        </div>
      );
    },
  },

  /* 2 — Ocean Gradient */
  {
    id: "ocean-gradient",
    name: "Ocean Gradient",
    thumbnail: { bg: "linear-gradient(135deg,#0A2342,#1D4E89)", accent: "#F59E0B" },
    background: (brand) => `linear-gradient(135deg, ${brand.primary_color || "#0A2342"} 0%, ${brand.secondary_color || "#1D4E89"} 100%)`,
    layout: (slide, brand, kind, meta) => {
      const text = brand.text_on_primary || "#FFFFFF";
      const soft = hexToRgba(text, 0.72);
      const accent = brand.accent_color || "#F59E0B";
      const heading = brand.font_heading || "Playfair Display, serif";
      if (kind === "cover") {
        return (
          <div style={{ padding: "120px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}>
            <div style={{ fontSize: 28, letterSpacing: "0.3em", textTransform: "uppercase", color: accent, fontWeight: 700, marginBottom: 48 }}>
              Dive Deeper
            </div>
            <h1 style={{ fontFamily: heading, fontSize: 116, lineHeight: 1, color: text, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
              {slide.headline}
            </h1>
            {slide.subhead && (
              <p style={{ fontSize: 42, lineHeight: 1.35, color: soft, marginTop: 44 }}>{slide.subhead}</p>
            )}
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ padding: "140px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "center", textAlign: "center" }}>
            <h1 style={{ fontFamily: heading, fontSize: 96, lineHeight: 1.06, color: text, fontWeight: 700, margin: 0 }}>{slide.headline}</h1>
            <div style={{
              display: "inline-block", padding: "36px 72px", marginTop: 68, background: accent,
              color: brand.primary_color || "#0A2342", fontSize: 42, fontWeight: 700, borderRadius: 999,
              alignSelf: "center",
            }}>
              {slide.cta}
            </div>
          </div>
        );
      }
      return (
        <div style={{ padding: "120px 100px", display: "flex", flexDirection: "column", height: "100%", justifyContent: "flex-start" }}>
          <div style={{ fontSize: 220, fontFamily: heading, fontWeight: 700, color: hexToRgba(accent, 0.35), lineHeight: 0.9, marginBottom: -60 }}>
            {String(meta.index).padStart(2, "0")}
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 84, lineHeight: 1.06, color: text, fontWeight: 700, margin: 0 }}>{slide.title}</h2>
          <p style={{ fontSize: 38, lineHeight: 1.42, color: soft, marginTop: 48 }}>{slide.body}</p>
        </div>
      );
    },
  },

  /* 3 — Bold Serif */
  {
    id: "bold-serif",
    name: "Bold Serif",
    thumbnail: { bg: "#F8F6F1", accent: "#B91C1C" },
    background: () => "#F8F6F1",
    layout: (slide, brand, kind, meta) => {
      const heading = brand.font_heading || "Playfair Display, serif";
      const text = "#111111";
      const accent = brand.accent_color || "#B91C1C";
      if (kind === "cover") {
        return (
          <div style={{ padding: "140px 100px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontSize: 32, fontStyle: "italic", color: accent, marginBottom: 48 }}>— a swipeable essay —</div>
            <h1 style={{ fontFamily: heading, fontSize: 132, lineHeight: 0.98, color: text, fontWeight: 900, letterSpacing: "-0.03em", margin: 0 }}>
              {slide.headline}
            </h1>
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ padding: "140px 100px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
            <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1.02, color: text, fontWeight: 900 }}>{slide.headline}</h1>
            <p style={{ fontSize: 44, marginTop: 56, color: accent, fontStyle: "italic", fontWeight: 600 }}>{slide.cta}</p>
          </div>
        );
      }
      return (
        <div style={{ padding: "120px 100px", height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 26, letterSpacing: "0.32em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 40 }}>
            Chapter {String(meta.index - 1).padStart(2, "0")}
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 88, lineHeight: 1.02, color: text, fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>
            {slide.title}
          </h2>
          <div style={{ width: 80, height: 4, background: accent, marginTop: 44 }} />
          <p style={{ fontSize: 38, lineHeight: 1.45, color: text, marginTop: 44, fontFamily: brand.font_body }}>{slide.body}</p>
        </div>
      );
    },
  },

  /* 4 — Newspaper Column */
  {
    id: "newspaper",
    name: "Newspaper Column",
    thumbnail: { bg: "#FAFAF7", accent: "#1F2937" },
    background: () => "#FAFAF7",
    layout: (slide, brand, kind, meta) => {
      const heading = "Georgia, 'Times New Roman', serif";
      const text = "#111111";
      const rule = "#111111";
      if (kind === "cover") {
        return (
          <div style={{ padding: "100px 90px", height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ borderTop: `4px solid ${rule}`, borderBottom: `1px solid ${rule}`, padding: "20px 0", display: "flex", justifyContent: "space-between", fontFamily: heading, fontSize: 24, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" }}>
              <span>{(brand.brand_name || "The Dive Brief").toUpperCase()}</span>
              <span>Issue №{String(meta.total)}</span>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
              <h1 style={{ fontFamily: heading, fontSize: 128, lineHeight: 1, color: text, fontWeight: 900, letterSpacing: "-0.02em", margin: 0 }}>
                {slide.headline}
              </h1>
            </div>
            {slide.subhead && <p style={{ fontFamily: heading, fontSize: 36, fontStyle: "italic", color: text, lineHeight: 1.3 }}>{slide.subhead}</p>}
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ padding: "140px 100px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
            <div style={{ fontFamily: heading, fontSize: 28, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, marginBottom: 40 }}>— Editorial —</div>
            <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1.02, color: text, fontWeight: 900 }}>{slide.headline}</h1>
            <p style={{ fontSize: 44, marginTop: 56, color: text, fontFamily: heading, fontStyle: "italic" }}>{slide.cta}</p>
          </div>
        );
      }
      return (
        <div style={{ padding: "100px 90px", height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ fontFamily: heading, fontSize: 22, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, color: text, marginBottom: 28 }}>
            No. {String(meta.index).padStart(2, "0")} · The Dive Brief
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 76, lineHeight: 1.05, color: text, fontWeight: 900, margin: 0, borderBottom: `2px solid ${rule}`, paddingBottom: 36 }}>
            {slide.title}
          </h2>
          <p style={{ fontFamily: heading, fontSize: 36, lineHeight: 1.5, color: text, marginTop: 44, columnCount: 1 }}>{slide.body}</p>
        </div>
      );
    },
  },

  /* 5 — Reef Photo Overlay (colour-block edition of Phase 3 photo template) */
  {
    id: "colour-block",
    name: "Colour Block",
    thumbnail: { bg: "linear-gradient(180deg,#0A2342 55%,#F59E0B 55%)", accent: "#FFFFFF" },
    background: (brand) => brand.primary_color || "#0A2342",
    layout: (slide, brand, kind, meta) => {
      const text = brand.text_on_primary || "#FFFFFF";
      const accent = brand.accent_color || "#F59E0B";
      const heading = brand.font_heading || "Playfair Display, serif";
      if (kind === "cover") {
        return (
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ flex: "0 0 60%", padding: "120px 100px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <h1 style={{ fontFamily: heading, fontSize: 116, lineHeight: 1, color: text, fontWeight: 700, margin: 0 }}>{slide.headline}</h1>
            </div>
            <div style={{ flex: "0 0 40%", background: accent, padding: "80px 100px", display: "flex", alignItems: "center", color: brand.primary_color || "#0A2342" }}>
              <div>
                <div style={{ fontSize: 24, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 800, marginBottom: 20 }}>Swipe to read →</div>
                {slide.subhead && <p style={{ fontSize: 40, lineHeight: 1.35, fontWeight: 500, margin: 0 }}>{slide.subhead}</p>}
              </div>
            </div>
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, padding: "120px 100px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1.02, color: text, fontWeight: 700, margin: 0 }}>{slide.headline}</h1>
            </div>
            <div style={{ background: accent, padding: "60px 100px", color: brand.primary_color || "#0A2342", textAlign: "center", fontSize: 42, fontWeight: 700 }}>
              {slide.cta}
            </div>
          </div>
        );
      }
      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ background: accent, color: brand.primary_color || "#0A2342", padding: "60px 100px", fontFamily: heading, fontSize: 88, fontWeight: 700, lineHeight: 1.05 }}>
            {String(meta.index).padStart(2, "0")}. {slide.title}
          </div>
          <div style={{ flex: 1, padding: "80px 100px", display: "flex", alignItems: "center" }}>
            <p style={{ fontSize: 40, lineHeight: 1.42, color: text, fontWeight: 400 }}>{slide.body}</p>
          </div>
        </div>
      );
    },
  },

  /* 6 — Highlight Ticker (viral IG native style with highlighted key words) */
  {
    id: "highlight-ticker",
    name: "Highlight Ticker",
    thumbnail: { bg: "#0F172A", accent: "#FBBF24" },
    background: () => "#0F172A",
    layout: (slide, brand, kind, meta) => {
      const text = "#FFFFFF";
      const highlight = brand.accent_color || "#FBBF24";
      const heading = brand.font_heading || "Inter, system-ui, sans-serif";
      if (kind === "cover") {
        return (
          <div style={{ padding: "120px 100px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "inline-block", background: highlight, color: "#0F172A", padding: "16px 32px", fontSize: 32, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", alignSelf: "flex-start", marginBottom: 48 }}>
              READ THIS
            </div>
            <h1 style={{ fontFamily: heading, fontSize: 132, lineHeight: 0.98, color: text, fontWeight: 900, letterSpacing: "-0.03em", margin: 0 }}>
              {slide.headline}
            </h1>
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ padding: "140px 100px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
            <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1.02, color: text, fontWeight: 900 }}>{slide.headline}</h1>
            <div style={{
              display: "inline-block", padding: "40px 68px", marginTop: 60, background: highlight, color: "#0F172A",
              fontSize: 44, fontWeight: 800, alignSelf: "center", letterSpacing: "-0.01em",
            }}>
              {slide.cta}
            </div>
          </div>
        );
      }
      return (
        <div style={{ padding: "120px 100px", height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 28, letterSpacing: "0.3em", textTransform: "uppercase", color: highlight, fontWeight: 800, marginBottom: 40 }}>
            {String(meta.index).padStart(2, "0")} / {String(meta.total).padStart(2, "0")}
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 96, lineHeight: 1.05, color: text, fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>
            <span style={{ background: highlight, color: "#0F172A", padding: "0 12px", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>{slide.title}</span>
          </h2>
          <p style={{ fontSize: 40, lineHeight: 1.42, color: text, marginTop: 56, fontWeight: 400 }}>{slide.body}</p>
        </div>
      );
    },
  },

  /* 7 — Photo · Full Bleed (Phase 3 — user photo w/ smart text placement) */
  {
    id: "photo-bleed",
    name: "Photo · Full Bleed",
    needsPhoto: true,
    thumbnail: { bg: "linear-gradient(135deg,#0F172A 30%,#F59E0B 100%)", accent: "#FFFFFF" },
    background: (brand) => brand.primary_color || "#0F172A",
    layout: (slide, brand, kind, meta) => {
      // Text renders in the SAFE zone (opposite the subject). A gradient
      // scrim in the safe half boosts legibility without dulling the photo
      // where the eye actually looks. If no photo is assigned yet, we
      // degrade to the Ocean Gradient look so the preview isn't blank.
      const photo = meta.photo;
      const safe = photo?.focal?.safe_zone || "bottom";
      const heading = brand.font_heading || "Playfair Display, serif";
      const text = "#FFFFFF";
      const accent = brand.accent_color || "#F59E0B";

      // Map safe_zone -> which flex-align to use for the text block, plus a
      // matching gradient scrim direction so the dark part sits under the text.
      const layoutStyle = _photoLayoutStyleFor(safe);

      return (
        <div style={{ position: "absolute", inset: 0, ...layoutStyle.container }}>
          {photo && (
            <img
              src={photo.url}
              alt=""
              crossOrigin="anonymous"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "cover",
                objectPosition: _cssObjectPos(photo.focal?.subject_zone || "center"),
              }}
            />
          )}
          {!photo && (
            <div style={{
              position: "absolute", inset: 0,
              background: `linear-gradient(135deg, ${brand.primary_color || "#0A2342"}, ${brand.secondary_color || "#1D4E89"})`,
            }} />
          )}
          {/* Scrim gradient in the safe zone */}
          <div style={{
            position: "absolute", inset: 0,
            background: layoutStyle.scrim,
            pointerEvents: "none",
          }} />
          {/* Text block */}
          <div style={{ position: "relative", zIndex: 2, padding: 100, ...layoutStyle.text, height: "100%", display: "flex", flexDirection: "column" }}>
            {kind === "cover" && (
              <div style={{ ...layoutStyle.inner }}>
                <div style={{ fontSize: 24, letterSpacing: "0.3em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 32 }}>
                  Swipe →
                </div>
                <h1 style={{ fontFamily: heading, fontSize: 116, lineHeight: 1, color: text, fontWeight: 700, margin: 0, letterSpacing: "-0.02em", textShadow: "0 2px 12px rgba(0,0,0,0.35)" }}>
                  {slide.headline}
                </h1>
                {slide.subhead && (
                  <p style={{ fontSize: 40, lineHeight: 1.35, color: text, marginTop: 32, textShadow: "0 2px 8px rgba(0,0,0,0.35)", opacity: 0.94 }}>
                    {slide.subhead}
                  </p>
                )}
              </div>
            )}
            {kind === "body" && (
              <div style={{ ...layoutStyle.inner }}>
                <div style={{ fontSize: 28, letterSpacing: "0.3em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 28, textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                  {String(meta.index).padStart(2, "0")} / {String(meta.total).padStart(2, "0")}
                </div>
                <h2 style={{ fontFamily: heading, fontSize: 88, lineHeight: 1.05, color: text, fontWeight: 700, margin: 0, textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                  {slide.title}
                </h2>
                <p style={{ fontSize: 36, lineHeight: 1.42, color: text, marginTop: 36, textShadow: "0 1px 6px rgba(0,0,0,0.4)", opacity: 0.94 }}>
                  {slide.body}
                </p>
              </div>
            )}
            {kind === "final" && (
              <div style={{ ...layoutStyle.inner, textAlign: "center", width: "100%" }}>
                <h1 style={{ fontFamily: heading, fontSize: 100, lineHeight: 1.02, color: text, fontWeight: 700, margin: 0, textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                  {slide.headline}
                </h1>
                <div style={{
                  display: "inline-block", padding: "32px 64px", marginTop: 48, background: accent,
                  color: brand.primary_color || "#0A2342", fontSize: 40, fontWeight: 700, borderRadius: 999,
                }}>
                  {slide.cta}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
  },

  /* 8 — Photo · Split (photo top 55%, text bottom 45% — text can't overlap) */
  {
    id: "photo-split",
    name: "Photo · Split",
    needsPhoto: true,
    thumbnail: { bg: "linear-gradient(180deg,#1D4E89 55%,#FFFFFF 55%)", accent: "#0A2342" },
    background: (brand) => brand.primary_color || "#0A2342",
    layout: (slide, brand, kind, meta) => {
      const photo = meta.photo;
      const heading = brand.font_heading || "Playfair Display, serif";
      const accent = brand.accent_color || "#F59E0B";
      const textCol = brand.text_on_light || "#0A2342";

      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: "0 0 55%", position: "relative", overflow: "hidden", background: brand.secondary_color || "#1D4E89" }}>
            {photo ? (
              <img
                src={photo.url} alt="" crossOrigin="anonymous"
                style={{
                  width: "100%", height: "100%", objectFit: "cover",
                  objectPosition: _cssObjectPos(photo.focal?.subject_zone || "center"),
                }}
              />
            ) : (
              <div style={{ height: "100%", background: `linear-gradient(135deg, ${brand.primary_color || "#0A2342"}, ${brand.secondary_color || "#1D4E89"})` }} />
            )}
          </div>
          <div style={{ flex: 1, background: "#FFFFFF", padding: "60px 80px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {kind === "cover" && (
              <>
                <div style={{ fontSize: 22, letterSpacing: "0.3em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 20 }}>Swipe →</div>
                <h1 style={{ fontFamily: heading, fontSize: 76, lineHeight: 1.02, color: textCol, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>{slide.headline}</h1>
                {slide.subhead && <p style={{ fontSize: 32, lineHeight: 1.35, color: textCol, marginTop: 24, opacity: 0.75 }}>{slide.subhead}</p>}
              </>
            )}
            {kind === "body" && (
              <>
                <div style={{ fontSize: 22, letterSpacing: "0.3em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 20 }}>
                  {String(meta.index).padStart(2, "0")} / {String(meta.total).padStart(2, "0")}
                </div>
                <h2 style={{ fontFamily: heading, fontSize: 60, lineHeight: 1.05, color: textCol, fontWeight: 700, margin: 0 }}>{slide.title}</h2>
                <p style={{ fontSize: 30, lineHeight: 1.44, color: textCol, marginTop: 24, opacity: 0.78 }}>{slide.body}</p>
              </>
            )}
            {kind === "final" && (
              <div style={{ textAlign: "center" }}>
                <h1 style={{ fontFamily: heading, fontSize: 68, lineHeight: 1.02, color: textCol, fontWeight: 700, margin: 0 }}>{slide.headline}</h1>
                <div style={{
                  display: "inline-block", padding: "24px 48px", marginTop: 32, background: accent,
                  color: brand.primary_color || "#0A2342", fontSize: 32, fontWeight: 700, borderRadius: 999,
                }}>
                  {slide.cta}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
  },

  /* 9 — Reel · Full Bleed Video (9:16 for Instagram Reels, server-composited) */
  {
    id: "reel-bleed",
    name: "Reel · Full Bleed",
    needsPhoto: false,       // this template needs a VIDEO, handled separately
    needsVideo: true,
    dimensions: { width: 1080, height: 1920 },
    thumbnail: { bg: "linear-gradient(180deg,#0F172A 0%,#F59E0B 100%)", accent: "#FFFFFF" },
    background: () => "#000000",   // video is layered on top by the renderer
    // The reel template renders text overlays ONLY. The video itself is
    // painted by the CarouselSlide renderer as a <video> element (preview)
    // and burned in server-side by FFmpeg (export). Text sits inside the
    // Instagram Reels safe zone so IG's UI never covers it.
    layout: (slide, brand, kind, meta) => {
      const heading = brand.font_heading || "Inter, system-ui, sans-serif";
      const accent = brand.accent_color || "#F59E0B";
      const text = "#FFFFFF";
      // Safe zone padding for a 1080×1920 canvas.
      const container = {
        position: "absolute", inset: 0,
        paddingTop: 220, paddingBottom: 380, paddingLeft: 60, paddingRight: 220,
        display: "flex", flexDirection: "column",
        justifyContent: kind === "cover" ? "center" : (kind === "final" ? "center" : "flex-start"),
        alignItems: "flex-start",
      };
      const shadow = { textShadow: "0 3px 14px rgba(0,0,0,0.55)" };
      if (kind === "cover") {
        return (
          <div style={container}>
            <div style={{ fontSize: 32, letterSpacing: "0.32em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 32, ...shadow }}>
              Watch this →
            </div>
            <h1 style={{ fontFamily: heading, fontSize: 108, lineHeight: 1, color: text, fontWeight: 900, margin: 0, letterSpacing: "-0.02em", ...shadow }}>
              {slide.headline}
            </h1>
            {slide.subhead && (
              <p style={{ fontSize: 38, lineHeight: 1.35, color: text, marginTop: 32, ...shadow, opacity: 0.94 }}>{slide.subhead}</p>
            )}
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={container}>
            <h1 style={{ fontFamily: heading, fontSize: 96, lineHeight: 1.05, color: text, fontWeight: 900, margin: 0, ...shadow }}>
              {slide.headline}
            </h1>
            <div style={{
              display: "inline-block", padding: "28px 56px", marginTop: 40, background: accent,
              color: "#0A2342", fontSize: 40, fontWeight: 800, borderRadius: 999,
            }}>
              {slide.cta}
            </div>
          </div>
        );
      }
      return (
        <div style={container}>
          <div style={{ fontSize: 30, letterSpacing: "0.32em", textTransform: "uppercase", color: accent, fontWeight: 800, marginBottom: 24, ...shadow }}>
            {String(meta.index).padStart(2, "0")} / {String(meta.total).padStart(2, "0")}
          </div>
          <h2 style={{ fontFamily: heading, fontSize: 84, lineHeight: 1.05, color: text, fontWeight: 900, margin: 0, ...shadow }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: 34, lineHeight: 1.42, color: text, marginTop: 28, ...shadow, opacity: 0.94 }}>{slide.body}</p>
        </div>
      );
    },
  },
];

// ----------------------------- Photo helpers -----------------------------

// Map a 3×3 focal zone to a CSS `object-position` value. This determines
// which part of the photo stays visible when `object-fit: cover` crops the
// aspect ratio to the slide's 4:5 portrait.
function _cssObjectPos(zone) {
  const map = {
    "top-left":     "0% 0%",
    "top":          "50% 0%",
    "top-right":    "100% 0%",
    "left":         "0% 50%",
    "center":       "50% 50%",
    "right":        "100% 50%",
    "bottom-left":  "0% 100%",
    "bottom":       "50% 100%",
    "bottom-right": "100% 100%",
  };
  return map[zone] || map.center;
}

// Given the SAFE zone (opposite of the subject), return the CSS scrim
// gradient direction, the text-block flex alignment and text alignment.
function _photoLayoutStyleFor(safeZone) {
  // Scrim: linear-gradient from opaque black at the safe-zone edge fading to
  // fully transparent at the subject-zone edge. Angle is derived from the
  // safe-zone position.
  const scrimBy = {
    "top":          "linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0) 75%)",
    "bottom":       "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0) 75%)",
    "left":         "linear-gradient(to right, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0) 75%)",
    "right":        "linear-gradient(to left, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0) 75%)",
    "top-left":     "linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0) 75%)",
    "top-right":    "linear-gradient(225deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0) 75%)",
    "bottom-left":  "linear-gradient(45deg,  rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0) 75%)",
    "bottom-right": "linear-gradient(315deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0) 75%)",
    "center":       "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0) 75%)",
  };
  // Vertical alignment: safe-zone top → justify-start, bottom → end, else centre.
  const vBy = { top: "flex-start", "top-left": "flex-start", "top-right": "flex-start",
                bottom: "flex-end", "bottom-left": "flex-end", "bottom-right": "flex-end" };
  // Horizontal alignment of the text block within the padded container.
  const hBy = { left: "flex-start", "top-left": "flex-start", "bottom-left": "flex-start",
                right: "flex-end", "top-right": "flex-end", "bottom-right": "flex-end" };
  const innerMaxW = { center: "100%" }[safeZone] || "85%";
  return {
    container: {},
    scrim: scrimBy[safeZone] || scrimBy.bottom,
    text: {
      justifyContent: vBy[safeZone] || "center",
      alignItems: hBy[safeZone] || "flex-start",
      textAlign: hBy[safeZone] === "flex-end" ? "right" : (hBy[safeZone] === "center" ? "center" : "left"),
    },
    inner: { maxWidth: innerMaxW },
  };
}

export const CAROUSEL_TEMPLATES = TEMPLATES;

export function getTemplate(id, customTemplates = []) {
  // Custom-extracted templates from Claude Vision live on the brand kit —
  // resolve them at render time so the picker + renderer share one source.
  if (typeof id === "string" && id.startsWith("custom-")) {
    const custom = (customTemplates || []).find(t => t.id === id);
    if (custom) return buildCustomTemplate(custom);
  }
  return TEMPLATES.find(t => t.id === id) || TEMPLATES[0];
}

// ---------- Custom template renderer -------------------------------------
//
// A custom template is a controlled style signature (colours + layout + font
// hint + accent style) extracted from a user-uploaded design reference. We
// map it to the same JSX rendering the built-in templates use — we're not
// pixel-copying, we're matching the style so DMS content lives inside a
// coherent brand aesthetic.
function buildCustomTemplate(sig) {
  const colours = sig.colours || {};
  const brandOverride = {
    primary_color: colours.primary || "#0A2342",
    secondary_color: colours.secondary || "#1D4E89",
    accent_color: colours.accent || "#F59E0B",
    text_on_primary: colours.text_on_primary || "#FFFFFF",
    text_on_light: colours.text_on_light || "#0A2342",
  };
  const fontHintMap = {
    serif_editorial: "Playfair Display, Georgia, serif",
    sans_modern: "Inter, system-ui, sans-serif",
    sans_bold_display: "Inter, system-ui, sans-serif",
    condensed: "'Barlow Condensed', Impact, sans-serif",
    mono: "'IBM Plex Mono', ui-monospace, monospace",
  };
  const fontHeading = fontHintMap[sig.font_hint] || "Inter, system-ui, sans-serif";
  const textAlign = sig.text_alignment || "left";

  const bgFor = () => {
    const p = brandOverride.primary_color;
    const s = brandOverride.secondary_color;
    switch (sig.background) {
      case "gradient_diagonal": return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
      case "gradient_vertical": return `linear-gradient(180deg, ${p} 0%, ${s} 100%)`;
      case "off_white_paper":   return "#F8F6F1";
      case "photo_scrim":       return p;   // photo layered on top if user assigns
      case "solid":
      default:                  return p;
    }
  };

  const surface = sig.background === "off_white_paper" ? "light" : "dark";
  const textColour = surface === "light" ? brandOverride.text_on_light : brandOverride.text_on_primary;
  const softText = surface === "light"
    ? `${brandOverride.text_on_light}B3`
    : `${brandOverride.text_on_primary}CC`;
  const accent = brandOverride.accent_color;

  const flexAlign = textAlign === "center" ? "center" : textAlign === "right" ? "flex-end" : "flex-start";

  const Accent = ({ label }) => {
    switch (sig.accent_style) {
      case "divider_bar":
        return <div style={{ width: 100, height: 6, background: accent, borderRadius: 3, marginBottom: 32, alignSelf: flexAlign }} />;
      case "ticker_pill":
        return (
          <div style={{
            display: "inline-block", background: accent, color: brandOverride.primary_color,
            padding: "16px 32px", fontSize: 28, fontWeight: 800, letterSpacing: "0.16em",
            textTransform: "uppercase", alignSelf: flexAlign, marginBottom: 40,
          }}>{label}</div>
        );
      case "colour_block":
        return (
          <div style={{
            display: "inline-block", background: accent, color: brandOverride.primary_color,
            padding: "20px 40px", fontSize: 28, fontWeight: 700, alignSelf: flexAlign, marginBottom: 40,
          }}>{label}</div>
        );
      case "outline_box":
        return (
          <div style={{
            display: "inline-block", border: `3px solid ${accent}`, color: accent,
            padding: "14px 28px", fontSize: 26, fontWeight: 700, alignSelf: flexAlign,
            letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 40,
          }}>{label}</div>
        );
      case "highlight_marker":
        return (
          <div style={{
            display: "inline-block", background: accent, color: brandOverride.primary_color,
            padding: "0 12px", fontSize: 32, fontWeight: 800, alignSelf: flexAlign, marginBottom: 32,
          }}>{label}</div>
        );
      case "none":
      default:
        return null;
    }
  };

  return {
    id: sig.id,
    name: sig.label || "Custom template",
    isCustom: true,
    signature: sig,
    thumbnail: {
      bg: sig.background === "gradient_diagonal"
        ? `linear-gradient(135deg, ${brandOverride.primary_color}, ${brandOverride.secondary_color})`
        : sig.background === "off_white_paper" ? "#F8F6F1" : brandOverride.primary_color,
      accent: accent,
    },
    background: bgFor,
    // Merge the extracted colours into `brand` so downstream CarouselSlide
    // renders with the reference palette. The layout function receives the
    // MERGED brand so users still get their logo + logo position.
    layout: (slide, userBrand, kind, meta) => {
      const brand = { ...userBrand, ...brandOverride, font_heading: fontHeading };
      const container = {
        padding: "120px 100px", height: "100%", display: "flex",
        flexDirection: "column",
        justifyContent: kind === "cover" ? "center" : kind === "final" ? "center" : "flex-start",
        alignItems: flexAlign, textAlign,
      };
      if (sig.layout === "split") {
        // Solid colour block occupies bottom 40%, headline stays in top 60%.
        const isDark = surface === "dark";
        return (
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{
              flex: "0 0 60%", padding: "120px 100px", display: "flex", flexDirection: "column",
              justifyContent: "flex-end", alignItems: flexAlign, textAlign,
            }}>
              <h1 style={{ fontFamily: fontHeading, fontSize: kind === "cover" ? 108 : 84, lineHeight: 1.02, color: textColour, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
                {kind === "body" ? slide.title : slide.headline}
              </h1>
            </div>
            <div style={{
              flex: "0 0 40%", background: accent, padding: "80px 100px", display: "flex",
              alignItems: "center", color: brandOverride.primary_color, justifyContent: flexAlign, textAlign,
            }}>
              <div>
                {kind === "cover" && <div style={{ fontSize: 24, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 800, marginBottom: 20 }}>Swipe to read →</div>}
                {kind === "body" && <div style={{ fontSize: 24, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 800, marginBottom: 20 }}>{String(meta.index).padStart(2, "0")} / {String(meta.total).padStart(2, "0")}</div>}
                {kind === "final" && <div style={{ fontSize: 40, fontWeight: 700 }}>{slide.cta}</div>}
                {kind !== "final" && (kind === "cover" ? slide.subhead : slide.body) && (
                  <p style={{ fontSize: 36, lineHeight: 1.35, fontWeight: 500, margin: 0 }}>
                    {kind === "cover" ? slide.subhead : slide.body}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      }
      // Default flow — one column, respecting textAlign + accent style.
      if (kind === "cover") {
        return (
          <div style={container}>
            <Accent label="Swipe →" />
            <h1 style={{ fontFamily: fontHeading, fontSize: 116, lineHeight: 1.0, color: textColour, fontWeight: 700, margin: 0, letterSpacing: "-0.02em", maxWidth: "95%" }}>
              {slide.headline}
            </h1>
            {slide.subhead && (
              <p style={{ fontSize: 40, lineHeight: 1.35, color: softText, marginTop: 32, maxWidth: "90%" }}>{slide.subhead}</p>
            )}
          </div>
        );
      }
      if (kind === "final") {
        return (
          <div style={{ ...container, justifyContent: "center", alignItems: "center", textAlign: "center" }}>
            <h1 style={{ fontFamily: fontHeading, fontSize: 100, lineHeight: 1.02, color: textColour, fontWeight: 700, margin: 0 }}>
              {slide.headline}
            </h1>
            <div style={{
              display: "inline-block", padding: "32px 64px", marginTop: 48, background: accent,
              color: brandOverride.primary_color, fontSize: 40, fontWeight: 700, borderRadius: 999,
            }}>{slide.cta}</div>
          </div>
        );
      }
      // body
      return (
        <div style={container}>
          <Accent label={`${String(meta.index).padStart(2, "0")} / ${String(meta.total).padStart(2, "0")}`} />
          <h2 style={{ fontFamily: fontHeading, fontSize: 88, lineHeight: 1.05, color: textColour, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: 40, lineHeight: 1.42, color: softText, marginTop: 40, maxWidth: "90%" }}>{slide.body}</p>
        </div>
      );
    },
  };
}

export { LogoBadge, SlideNumber };
